import { useState, useRef, useEffect } from 'react'
import { z } from 'zod'
import { Smartphone } from 'lucide-react'
import { showErrorData } from '@/lib/toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuthStore } from '@/features/auth/store/auth-store'
import { usePhone } from '../../hooks/use-phone'

type PhoneVerifyInputProps = {
  phoneNumber: string
  setPhoneNumber: React.Dispatch<React.SetStateAction<string>>
  onCodeSent?: () => void
  type?: 'register' | 'login' | 'bind'
}

const phoneSchema = z.string().regex(/^1[3-9]\d{9}$/, '请输入有效的手机号码')

export function PhoneVerifyInput({
  phoneNumber,
  setPhoneNumber,
  onCodeSent,
  type = 'login',
}: PhoneVerifyInputProps) {
  const { authSendSmsMutationAsync } = usePhone()
  const { handleSendSms } = useAuthStore((s) => s.handlers)
  const [count, setCount] = useState(0)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  const onSendSuccess = () => {
    onCodeSent?.()
    setCount(60)
    timerRef.current = setInterval(() => {
      setCount((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }

  const handleAction = async () => {
    // Validate phone number before sending
    const result = phoneSchema.safeParse(phoneNumber)
    if (!result.success) {
      showErrorData(result.error.issues[0].message)
      return
    }

    await handleSendSms(
      phoneNumber,
      type,
      authSendSmsMutationAsync,
      onSendSuccess
    )
  }

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  return (
    <div className='flex flex-col gap-6'>
      <div className='relative'>
        <Smartphone className='text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2' />
        <Input
          name='phone'
          className='bg-muted focus-visible:ring-primary h-12 border-none pl-10 focus-visible:ring-1'
          placeholder='请输入手机号'
          value={phoneNumber}
          onChange={(e) => {
            const value = e.target.value.replace(/\D/g, '').slice(0, 11)
            setPhoneNumber(value)
          }}
        />
      </div>

      <Button
        className='h-12 w-full text-base font-medium transition-all active:scale-[0.98]'
        variant={count > 0 ? 'outline' : 'default'}
        disabled={count > 0 || !phoneNumber}
        onClick={handleAction}
      >
        {count > 0 ? `${count}s` : '获取验证码'}
      </Button>
    </div>
  )
}
